import { useState } from "react";
import { Button, Spin, Empty, Input, Tag, Tooltip } from "antd";
import { MapPin, RefreshCw, Search, Clock, Navigation } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useWorkerLocations } from "../../../hooks/api/field-workers/hooks";
import { useTerm } from "../../../lib/terminology";

dayjs.extend(relativeTime);

function getStatusColor(recorded_at: string | null): { cls: string; label: string } {
  if (!recorded_at) return { cls: "bg-slate-100 text-slate-500", label: "Unknown" };
  const mins = dayjs().diff(dayjs(recorded_at), "minute");
  if (mins <= 15)  return { cls: "bg-emerald-100 text-emerald-700", label: "Active" };
  if (mins <= 60)  return { cls: "bg-amber-100 text-amber-700", label: "Recent" };
  return { cls: "bg-slate-100 text-slate-500", label: "Inactive" };
}

const EmployeeLocationsPage = () => {
  const term = useTerm();
  const [search, setSearch] = useState("");

  const { data: res, isLoading, refetch, dataUpdatedAt } = useWorkerLocations();
  const locations: any[] = res?.data ?? [];

  const filtered = locations.filter((loc) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (loc.user_name ?? "").toLowerCase().includes(s) ||
      (loc.user_email ?? "").toLowerCase().includes(s) ||
      (loc.address ?? "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-6 min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <MapPin size={20} style={{ color: "var(--color-primary)" }} />
            {term("worker", true)} Locations
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Live GPS locations from the field worker app
            {dataUpdatedAt ? ` · updated ${dayjs(dataUpdatedAt).fromNow()}` : ""}
          </p>
        </div>
        <Button
          icon={<RefreshCw size={14} />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          Refresh
        </Button>
      </div>

      {/* SEARCH */}
      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="text-slate-400 absolute left-3 top-2.5 z-10" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${term("worker", true).toLowerCase()}…`}
          className="pl-9 pr-4 h-10 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
        />
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Tracked", value: locations.length, color: "text-slate-700" },
          { label: "Active (last 15 min)", value: locations.filter((l) => dayjs().diff(dayjs(l.recorded_at), "minute") <= 15).length, color: "text-emerald-600" },
          { label: "Inactive (>1 hr)", value: locations.filter((l) => dayjs().diff(dayjs(l.recorded_at), "minute") > 60).length, color: "text-slate-400" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
            <MapPin size={18} className={s.color} />
            <div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* TABLE */}
      {isLoading ? (
        <div className="flex justify-center py-24"><Spin size="large" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 py-16">
          <Empty
            description={
              <div className="text-center">
                <p className="text-slate-600 font-medium">No location data available</p>
                <p className="text-slate-400 text-sm mt-1">
                  Location data is sent automatically by the field worker mobile app.
                </p>
              </div>
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 font-medium border-b border-slate-100">
                <th className="text-left px-5 py-3">{term("worker").toUpperCase()}</th>
                <th className="text-left py-3">LOCATION</th>
                <th className="text-left py-3">COORDINATES</th>
                <th className="text-left py-3">LAST SEEN</th>
                <th className="text-left py-3">STATUS</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((loc) => {
                const { cls, label } = getStatusColor(loc.recorded_at);
                return (
                  <tr key={loc.id} className="border-t border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-800">{loc.user_name?.trim() || "—"}</div>
                      <div className="text-xs text-slate-400">{loc.user_email}</div>
                      {loc.user_job_title && (
                        <div className="text-xs text-slate-400">{loc.user_job_title}</div>
                      )}
                    </td>
                    <td className="py-3 text-slate-600 max-w-[200px]">
                      <div className="text-sm truncate">{loc.address || "—"}</div>
                    </td>
                    <td className="py-3 font-mono text-xs text-slate-500">
                      <div>{loc.latitude != null ? Number(loc.latitude).toFixed(5) : "—"}, {loc.longitude != null ? Number(loc.longitude).toFixed(5) : "—"}</div>
                      {loc.accuracy != null && (
                        <div className="text-slate-400">±{Number(loc.accuracy).toFixed(0)}m</div>
                      )}
                      {loc.speed != null && (
                        <div className="text-slate-400">{(Number(loc.speed) * 3.6).toFixed(1)} km/h</div>
                      )}
                    </td>
                    <td className="py-3 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <Clock size={12} className="text-slate-400" />
                        <span className="text-sm">{loc.recorded_at ? dayjs(loc.recorded_at).fromNow() : "—"}</span>
                      </div>
                      {loc.recorded_at && (
                        <div className="text-xs text-slate-400">{dayjs(loc.recorded_at).format("DD/MM/YYYY HH:mm")}</div>
                      )}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                        {label}
                      </span>
                    </td>
                    <td className="py-3 pr-5">
                      {loc.latitude != null && loc.longitude != null && (
                        <Tooltip title="Open in Google Maps">
                          <a
                            href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <Navigation size={12} />
                            Map
                          </a>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeLocationsPage;
